import { useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { completePersonalSource } from "@/api/client/personal-sources"
import { Button } from "@/components/ui/button"
import { PERSONAL_SOURCES_QUERY_KEY } from "@/features/personalization/components/personal-sources-utils"

import {
  PERSONAL_SOURCE_RETURN_KEY,
  personalSourceFromState,
  safePersonalSourceReturnPath,
  takePersonalSourceAuthorizationResponse,
} from "../utils/personal-source-callback"

/**
 * Landing page of the Entra redirect for a personal-source consent.
 *
 * The backend owns the authorization code: it is read once from memory (the auth
 * provider captured it before MSAL could), removed from the URL, and forwarded to
 * `/api/personal-sources/<source>/callback` with the signed-in user's token. The
 * source comes from the OAuth `state`; an unknown state is a failure, never a guess.
 */
export function PersonalSourceCallbackPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const client = useQueryClient()
  const started = useRef(false)
  const [failed, setFailed] = useState(false)
  const [returnPath] = useState(() =>
    safePersonalSourceReturnPath(
      sessionStorage.getItem(PERSONAL_SOURCE_RETURN_KEY),
      location.origin
    )
  )

  useEffect(() => {
    if (started.current) return
    started.current = true

    const input = new URLSearchParams(takePersonalSourceAuthorizationResponse() ?? location.search)
    const response = new URLSearchParams()
    for (const key of ["code", "state", "error", "error_description"]) {
      const value = input.get(key)
      if (value) response.set(key, value)
    }

    // Remove the authorization code even when the exchange fails; never persist it.
    history.replaceState(history.state, "", location.pathname)
    sessionStorage.removeItem(PERSONAL_SOURCE_RETURN_KEY)

    const source = personalSourceFromState(response.get("state"))
    if (!source || (!response.has("code") && !response.has("error"))) {
      setFailed(true)
      return
    }

    void completePersonalSource(source, response.toString())
      .then(() => client.invalidateQueries({ queryKey: PERSONAL_SOURCES_QUERY_KEY }))
      .then(() => navigate(returnPath, { replace: true }))
      .catch(() => setFailed(true))
  }, [client, navigate, returnPath])

  return (
    <main className="mx-auto max-w-lg space-y-4 p-6">
      <h1 className="type-lead">
        {t("personalSources.callback.title", { defaultValue: "Connecting your account" })}
      </h1>

      <p role={failed ? "alert" : "status"}>
        {failed
          ? t("personalSources.callback.failed", {
              defaultValue:
                "The connection was not completed. Go back and try again with the same Microsoft account you use in this app.",
            })
          : t("personalSources.callback.connecting", {
              defaultValue: "Completing your connection…",
            })}
      </p>

      {failed ? (
        <Button onClick={() => navigate(returnPath, { replace: true })}>
          {t("personalSources.callback.return", { defaultValue: "Go back" })}
        </Button>
      ) : null}
    </main>
  )
}
